import React from 'react';
import {
  Modal,
  Text,
  Spinner,
} from '@cochlear-design-system/foundation';
import { PatientRecordContext } from '../contexts/PatientRecordContext';
import { ScheduleSRPortal } from '../../ScheduleSRPortal';
import {
  ACCESS_TOKEN_COOKIE,
  ID_TOKEN_COOKIE,
  envUrl,
  envUrlSRPortal,
  routes,
} from '../../../config';
import cookieService from '../../../services/cookieService';
import providerService from '../../../services/providerService';
import {
  formatPatientDetailsForSR,
  formatClinicDetailsForSR,
  formatDeviceDataForSR,
} from '../utils/serviceRequestFormatters';

const srPortalConfig = {
  enabled: true,
  scheduleSRPortalScriptPath: `${envUrlSRPortal}/static/chunks/remoteEntry.js`,
  apiBaseUrl: envUrlSRPortal,
};

export const CreateServiceRequestModal = () => {
  const {
    createServiceRequestModalTitle,
    showCreateServiceRequestModal,
    setShowCreateServiceRequestModal,
    createServiceRequestIframeLoaded,
    createServiceRequestUrl,
    createServiceRequestIframe,
    labels,
    useModuleFederationSR,
    patientId,
    deviceId,
    selectedDevice,
    patientDetails,
    associatedClinicData,
    providerCountryCode,
  } = React.useContext(PatientRecordContext);

  const accessToken = cookieService.getCookie(ACCESS_TOKEN_COOKIE);
  const idToken = cookieService.getCookie(ID_TOKEN_COOKIE);
  const { providerId } = providerService.getProviderDetails();
  const logoutUrl = `${envUrl}${routes.dpx.logout}`;

  // Render content based on region and deviceId availability
  const renderModalContent = () => {
    if (useModuleFederationSR) {
      // New Module Federation approach for CAM/APAC regions
      if (deviceId && selectedDevice) {
        // Transform all data to required format
        const formattedPatientDetails = formatPatientDetailsForSR(
          patientDetails,
        );
        const formattedClinicDetails = formatClinicDetailsForSR(
          providerService.getCurrentProvider(),
          associatedClinicData,
        );
        const formattedDeviceData = formatDeviceDataForSR(
          selectedDevice,
          deviceId,
        );

        // TODO: Remove temporary logging
        // eslint-disable-next-line no-console
        console.log(
          'CreateServiceRequestModal - Data being passed to SR Portal:',
          {
            countryCode: providerCountryCode,
            patientDetails: formattedPatientDetails,
            clinicDetails: formattedClinicDetails,
            patientId,
            deviceData: formattedDeviceData,
          },
        );

        return (
          <ScheduleSRPortal
            config={srPortalConfig}
            accessToken={accessToken}
            idToken={idToken}
            selectedProviderId={providerId}
            pageType="CreateServiceRequest"
            logoutUrl={logoutUrl}
            deviceData={formattedDeviceData}
            patientId={patientId}
            countryCode={providerCountryCode}
            patientDetails={formattedPatientDetails}
            clinicDetails={formattedClinicDetails}
          />
        );
      }
      return (
        <div
          style={{
            margin: 'auto',
            textAlign: 'center',
            marginTop: '3rem',
          }}
        >
          <Spinner animation="border" />
          <Text content="Loading..." />
        </div>
      );
    }

    // Legacy MCP iframe approach for EMEA/LATAM regions
    return (
      <>
        {!createServiceRequestIframeLoaded && (
          <div
            style={{
              margin: 'auto',
              textAlign: 'center',
              marginTop: '3rem',
            }}
          >
            <>
              <Spinner animation="border" />
              <Text
                content={
                  labels[
                    'labels.patientRecord.tabs.equipments.dataTable.options.loadingCreateSR'
                  ]
                }
              />
            </>
          </div>
        )}
        <iframe
          src={createServiceRequestUrl}
          id="modaliFrame-createSR"
          title={createServiceRequestModalTitle}
          width="100%"
          style={{ visibility: 'hidden' }}
          ref={createServiceRequestIframe}
        />
      </>
    );
  };

  return (
    <Modal
      title={createServiceRequestModalTitle}
      fullscreen
      show={showCreateServiceRequestModal}
      onHide={() => setShowCreateServiceRequestModal(false)}
    >
      <Modal.Header
        closeButton
        data-analytics="close_create_sr_modal"
      >
        <Modal.Title>{createServiceRequestModalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderModalContent()}</Modal.Body>
    </Modal>
  );
};
